import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, Play, FileText, Users2, ClipboardCheck, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal, SectionHeading, Eyebrow } from "../components/common";
import VideoModal from "../components/VideoModal";
import Seo from "../components/Seo";
import DiscoverBanner from "../components/DiscoverBanner";
import { assessments, videos, LINKS } from "../mock";

export default function Assessments() {
  const [video, setVideo] = useState(null);
  return (
    <div className="overflow-hidden">
      <Seo
        title="Child Cognitive Assessments: Nursery to Grade 12 | MapMyKid"
        description="Explore MapMyKid's science-backed cognitive assessments for Nursery to Grade 12. Measure intellect, memory, focus, decision-making speed, creativity and natural ability, with an instant, detailed cognitive profile."
      />
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-14">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-up">
          <Eyebrow>Assessments By Age</Eyebrow>
          <h1 className="mt-6 text-[28px] sm:text-[52px] font-extrabold leading-[1.06] text-slate-900">
            Find the <span className="brand-gradient-text">right assessment</span> for your child.
          </h1>
          <p className="mt-5 text-[18px] leading-relaxed text-slate-600">
            Every 60 minute assessment is science backed and tuned to the developmental stage of the child. Pick the grade band below to see exactly what is measured.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 space-y-10">
          {assessments.map((a, i) => (
            <Reveal key={a.id} delay={i * 60}>
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center rounded-[28px] border border-violet-100 bg-white overflow-hidden card-hover ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
                <div className="relative h-64 lg:h-full min-h-[300px] [direction:ltr]">
                  <img src={a.image} alt={a.title} className="absolute inset-0 h-full w-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                  <span className="absolute right-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[14px] font-bold text-violet-700 shadow-lg [direction:ltr]">{a.price} / assessment</span>
                </div>
                <div className="p-7 sm:p-9 [direction:ltr]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-violet-50 px-3 py-1 text-[14px] font-semibold text-violet-700">{a.tag}</span>
                    <span className="text-[15px] text-slate-500">{a.ages}</span>
                  </div>
                  <h2 className="mt-3 text-[28px] font-extrabold text-slate-900">{a.title}</h2>
                  <p className="mt-2 text-[16px] text-slate-600 leading-relaxed">{a.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1.5 text-[14px] font-semibold text-teal-700"><Sparkles className="h-4 w-4" /> {a.worksheets}</span>

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {a.parts.map((p) => (
                      <div key={p.name} className="rounded-2xl bg-violet-50/60 p-4">
                        <p className="flex items-center gap-2 text-[15px] font-bold text-violet-700"><ClipboardCheck className="h-4.5 w-4.5" /> {p.name}</p>
                        <p className="mt-1 text-[15px] text-slate-600">{p.detail}</p>
                      </div>
                    ))}
                  </div>

                  <p className="mt-6 text-[15px] font-bold uppercase tracking-wide text-slate-400">You receive</p>
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {a.indices.map((ix) => (
                      <li key={ix} className="flex items-start gap-2.5 text-[16px] text-slate-700"><Check className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" /> {ix}</li>
                    ))}
                  </ul>

                  <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <a href={LINKS.login} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full cta-gradient text-white rounded-full h-12 px-6 text-[16px] font-semibold">Register, Pay &amp; Start <ArrowRight className="ml-2 h-4.5 w-4.5" /></Button>
                    </a>
                    <Link to="/book">
                      <Button variant="outline" className="w-full rounded-full h-12 px-6 text-[16px] font-semibold border-violet-200 text-violet-700 hover:bg-violet-50">Leave Your Details</Button>
                    </Link>
                  </div>
                  <span className="mt-3 flex items-center gap-1.5 text-[15px] font-medium text-slate-500"><Clock className="h-4 w-4" /> {a.duration}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Watch & Learn" title="Understand the assessment in 2 minutes." />
          <div className="mt-12 mx-auto max-w-3xl">
            {videos.map((v, i) => (
              <Reveal key={v.title} delay={i * 90}>
                <button onClick={() => setVideo(v)} className="group relative w-full text-left rounded-3xl overflow-hidden border border-violet-100 bg-white card-hover">
                  <div className="relative h-64 sm:h-80">
                    <img src={v.poster} alt={v.title} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-slate-900/35" />
                    <span className="absolute inset-0 grid place-items-center"><span className="grid h-20 w-20 place-items-center rounded-full bg-white/90 text-violet-700 transition-transform group-hover:scale-110"><Play className="h-8 w-8 fill-violet-700" /></span></span>
                  </div>
                  <div className="p-6"><h3 className="text-[20px] font-bold text-slate-900">{v.title}</h3><p className="mt-1 text-[16px] text-slate-600">{v.desc}</p></div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <VideoModal open={!!video} onClose={() => setVideo(null)} video={video} />
      <DiscoverBanner />
    </div>
  );
}
